import React, {
  useCallback,
  useRef,
  useEffect
} from 'react';
import { Form } from 'antd';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProjectScheduleOfValuePDFRows,
  addProjectScheduleOfValuePDFRows,
  deleteProjectScheduleOfValuePDFRows,
} from '../../state/projects.actions';
import ScheduleOfValuesPDFPreviewFormInput from './ScheduleOfValuesPDFPreviewFormInput';
import HoverHelp from '../../../common/HoverHelp';

const TOP_SECTION_LIMIT = 8;
const TOP_SECTION_INITIAL_Y = 50;
const TOP_SECTION_INITIAL_X = 240;
const BOTTOM_SECTION_INITIAL_Y = 780;
const BOTTOM_SECTION_INITIAL_Y_LANDSCAPE = 560;
const BOTTOM_SECTION_INITIAL_X = 50;
const PDF_ROW_MIN_SPACING = 15;

const getPDFRowHeight = (value) => (
  Math.max(
    PDF_ROW_MIN_SPACING,
    PDF_ROW_MIN_SPACING * String(value ?? '').split('\n').length,
  )
);

const ScheduleOfValuesPDFPreviewForm = ({
  projectId,
  templateId,
  isLandscapeOn = false,
}) => {
  const dispatch = useDispatch();
  const scheduleOfValuePDFRows = useSelector(state => state.projects.scheduleOfValuePDFRows);
  const formRef = useRef(null);

  useEffect(() => {
    dispatch(getProjectScheduleOfValuePDFRows({
      projectId,
      templateId,
    }));
  }, [projectId, templateId]);

  const addFormItem = useCallback(() => {
    const currentRowCount = scheduleOfValuePDFRows?.length || 0;
    let defaultXPosition;
    let defaultYPosition;
    if (currentRowCount < TOP_SECTION_LIMIT) {
      const lastTopRow = scheduleOfValuePDFRows?.[currentRowCount - 1];
      defaultXPosition = TOP_SECTION_INITIAL_X;
      defaultYPosition = lastTopRow
        ? lastTopRow.yPosition + getPDFRowHeight(lastTopRow.value)
        : TOP_SECTION_INITIAL_Y;
    } else {
      const lastBottomRow = currentRowCount > TOP_SECTION_LIMIT
        ? scheduleOfValuePDFRows?.[currentRowCount - 1]
        : undefined;
      defaultXPosition = BOTTOM_SECTION_INITIAL_X;
      const bottomSectionInitialY = isLandscapeOn ? BOTTOM_SECTION_INITIAL_Y_LANDSCAPE : BOTTOM_SECTION_INITIAL_Y;
      defaultYPosition = lastBottomRow
        ? lastBottomRow.yPosition - getPDFRowHeight(lastBottomRow.value)
        : bottomSectionInitialY;
    }
    dispatch(addProjectScheduleOfValuePDFRows({
      projectId,
      templateId,
      payload: {
        key: '',
        value: '',
        xPosition: defaultXPosition,
        yPosition: defaultYPosition,
        spacing: null,
      },
    }));
  }, [projectId, templateId, scheduleOfValuePDFRows, isLandscapeOn]);

  const removeFormItem = useCallback((rowId) => {
    return () => {
      dispatch(deleteProjectScheduleOfValuePDFRows({
        projectId,
        templateId,
        rowId,
      }));
    };
  }, [projectId, templateId]);

  const xMax = isLandscapeOn ? 780 : 500;
  const yMax = isLandscapeOn ? 560 : 780;
  const infoText = (
    <>
      The first 8 fields will appear at the top of the PDF; any new fields after that will be placed at the bottom left.
      <br />
      <br />
      {`X ranges from 0-${xMax}, Y ranges from 0-${yMax}.`}
      <br />
      {`Top-right (${xMax},0), bottom-left (0,${yMax}).`}
    </>
  );

  return (
    <>
      <span>
        Fields will be initially added to the top of the pdf.
        <HoverHelp 
          content={infoText}
          placement="right"
          Icon={InfoCircleOutlined}
          iconProps={{ style: { marginLeft: 3 } }}
        />
      </span>
      <OnTraccrButton style={{ float: 'right' }} title="Add field to form" onClick={addFormItem}/>
      <Form ref={formRef}>
        <div className="schedule-of-value-pdf-form">
          {
            scheduleOfValuePDFRows.map((item, i) => (
              <div key={item.id} className="schedule-of-value-pdf-form-row">
                <span className="schedule-of-value-pdf-form-row-label">{`${i + 1}. `}</span>
                <Form.Item>
                  <ScheduleOfValuesPDFPreviewFormInput
                    title='Label'
                    projectId={projectId}
                    templateId={templateId}
                    pdfRow={item}
                    type={'text'}
                    property={'key'}
                    style={{ width: 300 }}
                  />
                </Form.Item>
                <Form.Item>
                  <ScheduleOfValuesPDFPreviewFormInput
                    title='Value'
                    projectId={projectId}
                    templateId={templateId}
                    pdfRow={item}
                    type={'text'}
                    property={'value'}
                    style={{ width: 300 }}
                    textarea
                  />
                </Form.Item>
                <Form.Item>
                  <ScheduleOfValuesPDFPreviewFormInput
                    title='X Co-ordinate'
                    projectId={projectId}
                    templateId={templateId}
                    pdfRow={item}
                    type={'number'}
                    property={'xPosition'}
                    style={{ width: 65 }}
                    isLandscapeOn={isLandscapeOn}
                  />
                </Form.Item>
                <Form.Item>
                  <ScheduleOfValuesPDFPreviewFormInput
                    title='Y Co-ordinate'
                    projectId={projectId}
                    templateId={templateId}
                    pdfRow={item}
                    type={'number'}
                    property={'yPosition'}
                    style={{ width: 65 }}
                    isLandscapeOn={isLandscapeOn}
                  />
                </Form.Item>
                <Form.Item>
                  <ScheduleOfValuesPDFPreviewFormInput
                    title='Spacing'
                    projectId={projectId}
                    templateId={templateId}
                    pdfRow={item}
                    type={'number'}
                    property={'spacing'}
                    style={{ width: 65 }}
                  />
                </Form.Item>
                <DeleteOutlined 
                  style={{ color: 'red', alignSelf: 'start', paddingTop: 10 }}
                  onClick={removeFormItem(item.id)}
                />
              </div>
            ))
          }
        </div>
      </Form>
    </>
  );
};

export default ScheduleOfValuesPDFPreviewForm;